import React from 'react';

import AliceSkillFormRedesign from 'client/blocks-redesign/Settings/AliceSkillForm';
import InstallableAppForm from 'client/blocks-redesign/Settings/InstallableAppForm';
import { SmartHomeAppForm } from 'client/blocks-redesign/Settings/SmartHomeAppForm';
import { isInDevelopment } from 'client/model/draft';
import {
    AliceSkillSettings,
    InstallableAppSettings,
    SmartHomeSettings,
    ThereminvoxSettings,
} from 'client/model/settings';
import {
    AliceApp,
    AliceSkill,
    AutoApp,
    Channel,
    InstallableApp,
    SmartHome,
    SmartScreenApp,
    ThereminvoxSkill,
    TvApp,
} from 'client/model/skill';
import { UserAgreement } from 'client/model/userAgreement';
import { showSkillsRedesign } from 'client/utils';
import block from 'propmods';

import AliceSkillForm from './channels/AliceSkillForm';
import SmartHomeForm from './channels/SmartHomeForm';
import { ThereminvoxForm } from './channels/ThereminvoxForm';

interface SettingsProps {
    aliceApp: AliceApp;
    setInstallableApp?: (app: InstallableApp) => void;
    refreshAliceApp?: () => void;
    isDraft: boolean;
    hasChanges: boolean;
    registerChanges: (hasChanges: boolean) => void;
    settings: AliceSkillSettings | InstallableAppSettings | ThereminvoxSettings | SmartHomeSettings;
    fetchedUserAgreementsDraft?: UserAgreement[];
    disabled: boolean;
}

const b = block('Settings');

export default class Settings extends React.Component<SettingsProps> {
    private get isEditable() {
        const { isDraft, aliceApp, disabled } = this.props;

        return isDraft && isInDevelopment(aliceApp) && !disabled;
    }

    public render() {
        return <div {...b(this)}>{this.renderSettings()}</div>;
    }

    private renderSettings = () => {
        const { aliceApp } = this.props;

        switch (aliceApp.channel) {
            case Channel.AliceSkill:
                return this.renderSkillSettings(aliceApp as AliceSkill);
            case Channel.SmartHome:
                return this.renderSmartHomeSettings(aliceApp as SmartHome);
            case Channel.Thereminvox:
                return this.renderThereminvoxSettings(aliceApp as ThereminvoxSkill);
            case Channel.AutoApp:
                return this.renderAppSettings(aliceApp as AutoApp);
            case Channel.TvApp:
                return this.renderAppSettings(aliceApp as TvApp);
            case Channel.SmartScreenApp:
                return this.renderAppSettings(aliceApp as SmartScreenApp);
        }
    };

    private renderAppSettings = (app: InstallableApp) => {
        const { isDraft, settings, registerChanges, hasChanges } = this.props;
        const draftModifier = isDraft ? '_draft' : '';
        const draftStatusModifier = `_status_${app.draft.status}`;
        const updatedAt = app.draft.updatedAt;

        return (
            <InstallableAppForm
                key={`InstallableAppForm${draftModifier}${draftStatusModifier}_${updatedAt}`}
                skillUseZora={app.useZora !== undefined ? app.useZora : true}
                settings={settings as InstallableAppSettings}
                setInstallableApp={this.props.setInstallableApp}
                disabled={!this.isEditable}
                isDraft={Boolean(this.props.isDraft)}
                registerChanges={registerChanges}
                hasChanges={hasChanges}
                app={app}
            />
        );
    };

    private renderSkillSettings = (skill: AliceSkill) => {
        const { isDraft, registerChanges, hasChanges } = this.props;
        const settings = this.props.settings as AliceSkillSettings;
        const isAccountLinkingTemplate = Boolean(
            isDraft && this.props.aliceApp.oauthAppId === this.props.aliceApp.draft.oauthAppId,
        );

        const draftModifier = isDraft ? '_draft' : '';
        const draftStatusModifier = `_status_${skill.draft.status}`;
        const updatedAt = skill.draft.updatedAt;

        const displayEmailVerificationError =
            Boolean(settings.publishingSettings.email) && !settings.publishingSettings.emailIsVerified;

        const displayBrandVerificationError =
            Boolean(settings.publishingSettings.brandVerificationWebsite) &&
            !settings.publishingSettings.brandIsVerified;

        const brandIsVerified = Boolean(settings.publishingSettings.brandIsVerified);

        if (showSkillsRedesign()) {
            return (
                <AliceSkillFormRedesign
                    key={`AliceSkillForm${draftModifier}${draftStatusModifier}_${updatedAt}`}
                    skillUseZora={skill.useZora !== undefined ? skill.useZora : true}
                    logoColor={settings.logo2 ? settings.logo2.meta.MainColor : ''}
                    settings={settings}
                    displayBrandVerificationError={displayBrandVerificationError}
                    brandIsVerified={brandIsVerified}
                    displayEmailVerificationError={displayEmailVerificationError}
                    disabled={!this.isEditable}
                    isDraft={Boolean(this.props.isDraft)}
                    registerChanges={registerChanges}
                    hasChanges={hasChanges}
                    refreshAliceApp={this.props.refreshAliceApp}
                    fetchedUserAgreements={this.props.fetchedUserAgreementsDraft}
                    isAccountLinkingTemplate={isAccountLinkingTemplate}
                    skill={skill}
                />
            );
        }

        return (
            <AliceSkillForm
                key={`AliceSkillForm${draftModifier}${draftStatusModifier}`}
                skillUseZora={skill.useZora !== undefined ? skill.useZora : true}
                logoUrl={settings.logo2 ? settings.logo2.url : ''}
                logoColor={settings.logo2 ? settings.logo2.meta.MainColor : ''}
                settings={settings}
                displayBrandVerificationError={displayBrandVerificationError}
                brandIsVerified={brandIsVerified}
                displayEmailVerificationError={displayEmailVerificationError}
                disabled={!this.isEditable}
                isDraft={Boolean(this.props.isDraft)}
                registerChanges={registerChanges}
                hasChanges={hasChanges}
                isAccountLinkingTemplate={isAccountLinkingTemplate}
                skill={skill}
            />
        );
    };

    private renderSmartHomeSettings = (skill: SmartHome) => {
        const { isDraft, registerChanges, hasChanges } = this.props;
        const settings = this.props.settings as SmartHomeSettings;
        const draftModifier = isDraft ? '_draft' : '';
        const draftStatusModifier = `_status_${skill.draft.status}`;

        const displayEmailVerificationError =
            Boolean(settings.publishingSettings.email) && !settings.publishingSettings.emailIsVerified;

        const displayBrandVerificationError =
            Boolean(settings.publishingSettings.brandVerificationWebsite) &&
            !settings.publishingSettings.brandIsVerified;

        const isAccountLinkingTemplate = Boolean(
            isDraft && this.props.aliceApp.oauthAppId === this.props.aliceApp.draft.oauthAppId,
        );

        if (showSkillsRedesign()) {
            return (
                <SmartHomeAppForm
                    key={`SmartHomeForm${draftModifier}${draftStatusModifier}`}
                    skillUseZora={skill.useZora !== undefined ? skill.useZora : true}
                    settings={settings as SmartHomeSettings}
                    displayBrandVerificationError={displayBrandVerificationError}
                    displayEmailVerificationError={displayEmailVerificationError}
                    disabled={!this.isEditable}
                    isDraft={Boolean(this.props.isDraft)}
                    refreshAliceApp={this.props.refreshAliceApp}
                    registerChanges={registerChanges}
                    hasChanges={hasChanges}
                    fetchedUserAgreements={this.props.fetchedUserAgreementsDraft}
                    skill={skill}
                />
            );
        }

        return (
            <SmartHomeForm
                key={`SmartHomeForm${draftModifier}${draftStatusModifier}`}
                skillUseZora={skill.useZora !== undefined ? skill.useZora : true}
                logoUrl={settings.logo2 ? settings.logo2.url : ''}
                logoColor={settings.logo2 ? settings.logo2.meta.MainColor : ''}
                settings={settings as SmartHomeSettings}
                displayBrandVerificationError={displayBrandVerificationError}
                displayEmailVerificationError={displayEmailVerificationError}
                disabled={!this.isEditable}
                isDraft={Boolean(this.props.isDraft)}
                registerChanges={registerChanges}
                hasChanges={hasChanges}
                isAccountLinkingTemplate={isAccountLinkingTemplate}
                skill={skill}
            />
        );
    };

    private renderThereminvoxSettings = (skill: ThereminvoxSkill) => {
        const { isDraft, registerChanges, settings, hasChanges } = this.props;
        const draftModifier = isDraft ? '_draft' : '';
        const draftStatusModifier = `_status_${skill.draft.status}`;

        return (
            <ThereminvoxForm
                key={`ThereminvoxForm${draftModifier}${draftStatusModifier}`}
                settings={settings as ThereminvoxSettings}
                disabled={!this.isEditable}
                hasChanges={hasChanges}
                registerChanges={registerChanges}
                skill={skill}
            />
        );
    };
}
